<template>
  <view-container>
    <browse-list
      :game="game"
      :items="collections"
      :loading="loadingCollections"
      :browse-id="browseId"
      :total="collections?.result_total"
      :sort-options="COLLECTION_SORT_OPTIONS"
      :card="COLLECTION_CARD"
      @fetch:paginate-next="fetchPaginateNext"
      @fetch:query="fetchCollections"
    />
  </view-container>
</template>

<script>
import { USER_COLLECTION_BROWSE_ROUTE } from '@config/routeNames.js'
import { usePermissions, useAsync, useRoute } from '@composables'
import ViewContainer from '@components/View/ViewContainer.vue'
import { mergeApiResultData } from '@helpers/requestAll.js'
import { COLLECTION_SORT_OPTIONS } from '@config/sort.js'
import BrowseList from '@components/List/BrowseList.vue'
import { getCollectionsForGameRequest } from '@services'
import { KEYS_COLLECTIONS } from '@config/storeKeys.js'
import { COLLECTION_CARD } from '@config/card.js'
import { filterSettings } from '@config/game.js'
import { computed, provide, watch } from 'vue'
import { store } from '@stores'
import pick from 'lodash/pick'
import useFilter, {
  normalizeCollectionFilters,
  getDateFilter,
  getAPIFilter,
} from '@composables/useFilter.js'
import {
  collectionStore,
  userStatsStore,
  gameStore,
  authStore,
  userStore,
} from '@stores'

export default {
  components: {
    ViewContainer,
    BrowseList,
  },
  setup() {
    const { getPublicGame } = gameStore()
    const { addCollections, getCollections } = collectionStore()
    const { routeQuery, getUserNameId, routeName } = useRoute()
    const { isGameModerator } = usePermissions()

    const { addUserStats } = userStatsStore()
    const userNameId = getUserNameId().value
    const { isLoggedIn, user: myUser } = authStore()
    const user = userStore().getUser(userNameId)
    const { generateId } = store()
    const game = getPublicGame()

    const browseId = generateId(
      KEYS_COLLECTIONS,
      `${game.value.name_id}${userNameId || ''}`
    )
    const { setFilter, settingsTags, resetFilter } = useFilter(browseId)

    provide('settingsTags', settingsTags)

    const collections = getCollections(
      `${game.value.name_id}${userNameId || ''}`
    )

    const isMe = computed(
      () => isLoggedIn.value && myUser.value?.name_id === userNameId
    )

    const showAdminFilters = computed(() => isGameModerator.value || isMe.value)

    const {
      data: collectionsData,
      loading: loadingCollections,
      run: runFetchCollections,
      error: collectionErrors,
    } = useAsync((query) => getCollectionsForGameRequest(query))

    watch(
      [game, showAdminFilters],
      ([_game, _showAdminFilters]) => {
        if (_game) {
          const picks = ['tags', 'tags-not-in', 'date_live']
          if (_showAdminFilters) picks.push('admin')

          const queryTags = routeQuery.value.tags
          const dateTags = routeQuery.value.date_live || ''
          const filters = game.value.collection_tag_options.filter(
            (tagGroup) => !tagGroup.hidden || isGameModerator.value
          )

          setFilter(
            normalizeCollectionFilters(
              filters,
              pick(routeQuery.value, picks),
              _showAdminFilters
            ),
            !queryTags && !dateTags
          )
        }
      },
      {
        immediate: true,
      }
    )

    watch(() => filterSettings.value.tags, resetFilter)

    function getQuery(query = {}) {
      const _query = { ...query }
      delete _query.platforms

      if (userNameId) _query.submitted_by = user.value.id

      // Remap collection tag properties
      const tagQuery = _query.tags
      if (tagQuery) {
        delete _query.tags
        _query.collection_tags = tagQuery
      }

      const tagExcludeQuery = _query['tags-not-in']
      if (tagExcludeQuery) {
        delete _query['tags-not-in']
        _query['collection_tags-not-in'] = tagExcludeQuery
      }

      getAPIFilter(_query)
      getDateFilter(_query)

      return _query
    }

    async function fetchCollections(query) {
      const _query = getQuery(query)

      await runFetchCollections(_query)

      if (!collectionErrors.value) {
        _updateStore(collectionsData.value, query)

        if (routeName.value === USER_COLLECTION_BROWSE_ROUTE) {
          const total = collectionsData.value.result_total
          addUserStats(userNameId, { collection_count: total })
        }
      }
    }

    async function fetchPaginateNext(query) {
      const _query = getQuery(query)

      await runFetchCollections(_query)

      if (!collectionErrors.value) {
        _updateStore(
          mergeApiResultData(collections.value, collectionsData.value),
          query
        )
      }
    }

    function _updateStore(data, query) {
      addCollections(`${game.value.name_id}${userNameId || ''}`, data, query)
    }

    return {
      COLLECTION_SORT_OPTIONS,
      loadingCollections,
      fetchPaginateNext,
      fetchCollections,
      COLLECTION_CARD,
      collections,
      browseId,
      game,
    }
  },
}
</script>
